import { Injectable, ComponentRef, Injector, Type, ApplicationRef, createComponent, EnvironmentInjector, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CYModalComponent } from './modal.component';
import { CYModalConfig, CYModalRef, CYCurrentModalContext, CY_CURRENT_MODAL } from './modal.interface';

/**
 * Servicio para crear y gestionar modales dinámicos
 *
 * Este servicio permite abrir cualquier componente como modal,
 * similar a Angular Material Dialog o Ionic Modal Controller.
 *
 * Características principales:
 * - Creación dinámica de componentes
 * - Gestión automática del DOM y ciclo de vida
 * - Paso de datos bidireccional
 * - Animaciones suaves de entrada y salida
 * - Soporte para promesas (afterClosed)
 * - Configuración flexible
 * - **SOPORTE PARA MÚLTIPLES MODALES SIMULTÁNEOS**
 * - Sistema de stack (pila) con z-index automático
 * - Gestión independiente de cada modal
 *
 * @example
 * ```typescript
 * constructor(private modalService: CYModalService) {}
 *
 * openModal() {
 *   const modalRef = this.modalService.open(MyComponent, {
 *     width: '500px',
 *     data: { mensaje: 'Hola mundo' }
 *   });
 *
 *   modalRef.afterClosed().then(resultado => {
 *     console.log('Modal cerrado:', resultado);
 *   });
 * }
 * ```
 */
/** Interface para gestionar cada instancia de modal */
interface ModalInstance {
  id: string;
  modalRef: ComponentRef<CYModalComponent>;
  componentRef: ComponentRef<any> | null;
  resolveAfterClosed: ((value: any) => void) | null;
  zIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class CYModalService {
  /** Stack de modales activos (el último es el que está encima) */
  private modalStack: ModalInstance[] = [];

  /** Contador para z-index base (cada modal se apila encima del anterior) */
  private baseZIndex = 1000;

  /** Contador para IDs únicos de modales */
  private modalIdCounter = 0;

  /** Inyección de dependencias */
  private document = inject(DOCUMENT);
  private applicationRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private environmentInjector = inject(EnvironmentInjector);

  /** Referencia estática para acceso desde componentes hijo */
  private static currentService: CYModalService;

  constructor() {
    CYModalService.currentService = this;
  }

  /**
   * Método estático para cerrar el modal más reciente desde cualquier componente
   * @param result - Resultado opcional para pasar al afterClosed
   */
  static closeModal(result?: any) {
    if (CYModalService.currentService) {
      CYModalService.currentService.closeTopModal(result);
    }
  }

  /**
   * Método estático para cerrar todos los modales
   */
  static closeAllModals() {
    if (CYModalService.currentService) {
      CYModalService.currentService.closeAll();
    }
  }

  /**
   * Abre un componente como modal
   *
   * @param component - Tipo del componente a mostrar en el modal
   * @param config - Configuración opcional del modal (tamaño, datos, etc.)
   * @returns CYModalRef con referencia al componente y métodos de control
   *
   * @example
   * ```typescript
   * const modalRef = this.modalService.open(AddCardComponent, {
   *   width: '600px',
   *   data: { usuario: this.user },
   *   disableClose: true
   * });
   * ```
   */
  open<T>(component: Type<T>, config?: CYModalConfig): CYModalRef<T> {
    // Generar ID único para este modal
    const modalId = `modal-${++this.modalIdCounter}`;

    // Calcular z-index para este modal (cada nuevo modal está encima del anterior)
    const zIndex = this.baseZIndex + this.modalStack.length;

    // Crear el componente modal
    const modalRef = createComponent(CYModalComponent, {
      environmentInjector: this.environmentInjector
    });

    // Configurar el modal
    if (config) {
      modalRef.instance.config = config;
    }

    // Configurar z-index del modal
    modalRef.location.nativeElement.style.zIndex = zIndex.toString();

    // Agregar el modal al DOM como hijo del body
    this.document.body.appendChild(modalRef.location.nativeElement);

    // Detectar cambios para que se renderice
    this.applicationRef.attachView(modalRef.hostView);

    // Crear la instancia del modal para el stack
    const modalInstance: ModalInstance = {
      id: modalId,
      modalRef: modalRef,
      componentRef: null,
      resolveAfterClosed: null,
      zIndex: zIndex
    };

    // Agregar al stack
    this.modalStack.push(modalInstance);

    // Esperar a que se renderice el modal antes de crear el componente hijo
    setTimeout(() => {
      const currentModal = this.getModalById(modalId);
      if (currentModal?.modalRef) {
        try {
          // Crear el contexto del modal actual para inyección
          const modalContext: CYCurrentModalContext = {
            id: modalId,
            close: (result?: any) => this.closeModal(modalId, result),
            dismiss: (result?: any) => this.closeModal(modalId, result),
            closeWithAnimation: () => {
              if (currentModal.modalRef) {
                currentModal.modalRef.instance.closeWithAnimation();
              }
            }
          };

          // Crear un injector personalizado que incluye el contexto del modal
          const customInjector = Injector.create({
            providers: [
              {
                provide: CY_CURRENT_MODAL,
                useValue: modalContext
              }
            ],
            parent: this.injector
          });

          // Crear el componente hijo dentro del modal con el injector personalizado
          currentModal.componentRef = currentModal.modalRef.instance.attachComponent(component, customInjector);

          // Si hay datos, asignarlos al componente
          if (config?.data) {
            Object.assign(currentModal.componentRef.instance, config.data);
          }

          // Configurar métodos de cierre del componente hijo (método legacy)
          this.setupChildComponentMethods(modalId);

          // Detectar cambios en el componente hijo
          currentModal.componentRef.changeDetectorRef.detectChanges();
        } catch (error) {
          console.error('Error creating child component:', error);
          this.closeModal(modalId);
        }
      }
    }, 0);

    // Crear la promesa para afterClosed
    const afterClosedPromise = new Promise<any>(resolve => {
      modalInstance.resolveAfterClosed = resolve;
    });

    return {
      componentInstance: modalInstance.componentRef?.instance,
      close: (result?: any) => this.closeModal(modalId, result),
      closeWithAnimation: () => {
        if (modalInstance.modalRef) {
          modalInstance.modalRef.instance.closeWithAnimation();
        }
      },
      afterClosed: () => afterClosedPromise
    };
  }

  /**
   * Obtiene una instancia de modal por su ID
   * @private
   */
  private getModalById(modalId: string): ModalInstance | undefined {
    return this.modalStack.find(modal => modal.id === modalId);
  }

  /**
   * Cierra el modal más reciente (el que está encima del stack)
   */
  closeTopModal(result?: any): void {
    if (this.modalStack.length === 0) return;

    const topModal = this.modalStack[this.modalStack.length - 1];
    this.closeModal(topModal.id, result);
  }

  /**
   * Cierra todos los modales
   */
  closeAll(): void {
    // Crear una copia del stack para evitar problemas durante la iteración
    const modalsToClose = [...this.modalStack];
    modalsToClose.forEach(modal => {
      this.closeModal(modal.id);
    });
  }

  /**
   * Configura los métodos close y dismiss del componente hijo
   * para que cierren el modal con animación
   * @private
   */
  private setupChildComponentMethods(modalId: string) {
    const modalInstance = this.getModalById(modalId);
    if (!modalInstance?.componentRef?.instance || !modalInstance.modalRef) return;

    // Variable para almacenar el resultado del cierre
    let closeResult: any = undefined;

    // Configurar método dismiss si existe
    if (typeof modalInstance.componentRef.instance.dismiss === 'function') {
      modalInstance.componentRef.instance.dismiss = (result?: any) => {
        closeResult = result;
        modalInstance.modalRef!.instance.closeWithAnimation();
      };
    }

    // Configurar método close si existe
    if (typeof modalInstance.componentRef.instance.close === 'function') {
      modalInstance.componentRef.instance.close = (result?: any) => {
        closeResult = result;
        modalInstance.modalRef!.instance.closeWithAnimation();
      };
    } else {
      // Crear método close si no existe
      modalInstance.componentRef.instance.close = (result?: any) => {
        closeResult = result;
        modalInstance.modalRef!.instance.closeWithAnimation();
      };
    }

    // Configurar el callback del modal para usar el resultado
    modalInstance.modalRef.instance.close = () => {
      this.closeModal(modalId, closeResult);
    };
  }

  /**
   * Cierra un modal específico por su ID
   *
   * @param modalId - ID del modal a cerrar
   * @param result - Resultado opcional para pasar a afterClosed
   */
  closeModal(modalId: string, result?: any): void {
    const modalIndex = this.modalStack.findIndex(modal => modal.id === modalId);
    if (modalIndex === -1) return;

    const modalInstance = this.modalStack[modalIndex];

    // Remover y destruir el modal
    if (modalInstance.modalRef) {
      this.applicationRef.detachView(modalInstance.modalRef.hostView);
      if (modalInstance.modalRef.location.nativeElement.parentNode) {
        modalInstance.modalRef.location.nativeElement.parentNode.removeChild(modalInstance.modalRef.location.nativeElement);
      }
      modalInstance.modalRef.destroy();
    }

    // Destruir el componente hijo
    if (modalInstance.componentRef) {
      modalInstance.componentRef.destroy();
    }

    // Resolver la promesa afterClosed
    if (modalInstance.resolveAfterClosed) {
      modalInstance.resolveAfterClosed(result);
    }

    // Remover del stack
    this.modalStack.splice(modalIndex, 1);
  }

  /**
   * Cierra el modal más reciente (DEPRECATED - usar closeTopModal)
   * Mantenido por compatibilidad con código existente
   *
   * @param result - Resultado opcional para pasar a afterClosed
   */
  close(result?: any): void {
    this.closeTopModal(result);
  }

  /**
   * Obtiene el número de modales activos
   */
  getActiveModalsCount(): number {
    return this.modalStack.length;
  }

  /**
   * Verifica si hay modales abiertos
   */
  hasOpenModals(): boolean {
    return this.modalStack.length > 0;
  }

  /**
   * Obtiene información de todos los modales activos
   */
  getActiveModalsInfo(): { id: string, zIndex: number }[] {
    return this.modalStack.map(modal => ({
      id: modal.id,
      zIndex: modal.zIndex
    }));
  }
}
